import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { get, throttle, compact, isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { SEOModel } from 'client/data/models/seo';
import { useOnScreen } from 'site-modules/shared/hooks/use-on-screen';
import { ResearchType } from 'site-modules/editorial/pages/research-article/research-type/research-type';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { EngagementHandler } from 'client/engagement-handlers';
import { AdsZoneEngagementHandler } from 'client/engagement-handlers/ads-zone-engagement-handler/ads-zone-engagement-handler';
import { getResearchArticlePageName } from 'site-modules/editorial/utils/research-article';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { useArticleUrlChange } from './use-article-url-change';
import { EndlessScrollFooter } from './endless-scroll-footer';

import './endless-scroll.scss';

const ENDLESS_SCROLL_SIZE = 30;
const ENDLESS_SCROLL_FOOTER_START_INDEX = 1;
const NEXT_ARTICLE_OBSERVER_OPTIONS = { rootMargin: '100%' };

EngagementHandler.register(AdsZoneEngagementHandler);

export function EndlessScrollArticleWrapper({ children, articleParams, index, onSetActive, setModelValue }) {
  const article = useRef();
  const [isOnScreen] = useOnScreen(article);

  const pathname = articleParams.splat
    ? `/${articleParams.articleCategory}/${articleParams.splat}/${articleParams.articleId}.html`
    : `/${articleParams.articleCategory}/${articleParams.articleId}.html`;

  useArticleUrlChange(isOnScreen, { ...articleParams, pathname }, setModelValue);

  useEffect(() => {
    if (isOnScreen) {
      onSetActive(index);
    }
  }, [index, isOnScreen, onSetActive]);

  useEffect(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: 'endless_scroll_display_next_article',
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
      },
    });
  }, []);

  return <div ref={article}>{children}</div>;
}

EndlessScrollArticleWrapper.propTypes = {
  children: PropTypes.node,
  articleParams: PropTypes.shape({
    articleCategory: PropTypes.string,
    splat: PropTypes.string,
    articleId: PropTypes.string,
    page: PropTypes.shape({
      pageName: PropTypes.string,
      categoryName: PropTypes.string,
    }),
  }).isRequired,
  setModelValue: PropTypes.func.isRequired,
  onSetActive: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

EndlessScrollArticleWrapper.defaultProps = {
  children: null,
};

export function EndlessScrollUI({ articlesParamsList, hostArticleParams, hostPathname, hostPage, setModelValue }) {
  const [articlesParamsLength, setArticlesParamsLength] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const loader = useRef();
  const endlessScroll = useRef();
  const pathname = useRef(hostPathname);
  const page = useRef(hostPage);

  const [isInEndlessScroll, disconnectIsInEndlessScroll] = useOnScreen(endlessScroll);
  const [shouldPreloadNextArticle, disconnectShouldPreloadNextArticle] = useOnScreen(
    loader,
    NEXT_ARTICLE_OBSERVER_OPTIONS
  );

  useArticleUrlChange(
    !isInEndlessScroll,
    { ...hostArticleParams, pathname: pathname.current, page: page.current },
    setModelValue
  );

  const throttledPreloadNextPage = useCallback(
    throttle(() => {
      setArticlesParamsLength(currentLength => currentLength + 1);
    }, 1000),
    []
  );

  useEffect(() => {
    if (shouldPreloadNextArticle && !isEmpty(articlesParamsList)) {
      throttledPreloadNextPage();
    }
  }, [shouldPreloadNextArticle, articlesParamsList, throttledPreloadNextPage]);

  useEffect(() => {
    if (articlesParamsLength >= get(articlesParamsList, 'length', 0)) {
      disconnectIsInEndlessScroll();
      disconnectShouldPreloadNextArticle();
    }
  }, [articlesParamsLength, articlesParamsList, disconnectIsInEndlessScroll, disconnectShouldPreloadNextArticle]);

  return isEmpty(articlesParamsList) ? null : (
    <div className="endless-scroll bg-white" ref={endlessScroll} data-nosnippet>
      {articlesParamsList.slice(0, articlesParamsLength).map((params, index) => (
        <ErrorBoundary key={JSON.stringify(params)}>
          <EndlessScrollArticleWrapper
            articleParams={params}
            index={index}
            onSetActive={setActiveIndex}
            setModelValue={setModelValue}
          >
            <ResearchType {...params} isEndlessScrollArticle />
          </EndlessScrollArticleWrapper>
        </ErrorBoundary>
      ))}
      {articlesParamsLength < ENDLESS_SCROLL_SIZE && <div ref={loader} className="loader-placeholder" />}
      <PageLinksWidget className="container mb-3" />
      <EndlessScrollFooter isActive={activeIndex >= ENDLESS_SCROLL_FOOTER_START_INDEX} />
    </div>
  );
}

EndlessScrollUI.propTypes = {
  articlesParamsList: PropTypes.arrayOf(PropTypes.shape({})),
  hostArticleParams: PropTypes.shape({
    articleCategory: PropTypes.string,
    splat: PropTypes.string,
    articleId: PropTypes.string,
  }).isRequired,
  hostPathname: PropTypes.string,
  hostPage: PropTypes.shape({
    pageName: PropTypes.string.isRequired,
    categoryName: PropTypes.string.isRequired,
  }).isRequired,
  setModelValue: PropTypes.func.isRequired,
};

EndlessScrollUI.defaultProps = {
  articlesParamsList: [],
  hostPathname: '',
};

export const propsAreEqual = (prevProps, nextProps) => {
  const { articlesParamsList: prevList } = prevProps;
  const { articlesParamsList: nextList } = nextProps;
  return get(prevList, 'length', 0) === get(nextList, 'length', 0);
};

const EndlessScrollMemoized = React.memo(EndlessScrollUI, propsAreEqual);

export const stateToPropsConfig = {
  articlesParamsList: bindToPath(props => `similarPages["${props.hostPathname}"]`, SEOModel, results =>
    compact(
      results.map(({ location }) => {
        const params = compact(location.split('/'));
        let articleCategory = '';
        let splat = '';
        let articleId = '';

        if (params.length === 3) {
          [articleCategory, splat, articleId] = params;
        } else if (params.length === 2) {
          [articleCategory, articleId] = params;
        } else {
          return null;
        }

        return {
          articleCategory,
          splat,
          articleId,
          page: {
            pageName: getResearchArticlePageName(articleCategory),
            categoryName: 'advice',
          },
        };
      })
    ).slice(0, ENDLESS_SCROLL_SIZE)
  ),
};

const EndlessScrollConnected = connectToModel(EndlessScrollMemoized, stateToPropsConfig);

function EndlessScrollWrapper({ hostPathname, ...props }) {
  const pathname = useRef(hostPathname);

  return <EndlessScrollConnected {...props} hostPathname={pathname.current} />;
}

export const mapStateToProps = state => ({
  hostPathname: get(state, 'pageContext.location.pathname'),
  hostPage: {
    pageName: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
    categoryName: get(state, 'pageContext.legacy.categoryName') || get(state, 'pageContext.page.category'),
  },
});

export const EndlessScroll = connect(mapStateToProps)(EndlessScrollWrapper);
